'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import { useEffect, useRef, useState } from 'react';
import { ColumnsIcon } from '@phosphor-icons/react';
import { columnTooltip, type ResolvedColumn } from '@/shared/mobile-service/lib/columns';

interface ColumnSelectorProps {
  /** All columns the user may toggle. */
  columns: ResolvedColumn[];
  /** Fields currently shown in the grid. */
  visibleFields: string[];
  onChange: (nextVisibleFields: string[]) => void;
}

/**
 * Popover listing every selectable table column with a checkbox for visibility.
 * Keeps at least one column checked so the grid never goes empty.
 */
export function ColumnSelector({ columns, visibleFields, onChange }: ColumnSelectorProps) {
  const t = useTimePilotT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const visible = new Set(visibleFields);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (columns.length === 0) return null;

  const toggle = (field: string) => {
    const next = new Set(visible);
    if (next.has(field)) {
      if (next.size <= 1) return;
      next.delete(field);
    } else {
      next.add(field);
    }
    // Preserve the canonical column order from ``columns``.
    onChange(columns.filter((c) => next.has(c.field)).map((c) => c.field));
  };

  const shownCount = visibleFields.length;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-md border border-white/20 bg-white/5 px-2.5 py-1.5 text-sm text-white/80 hover:bg-white/10"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <ColumnsIcon size={16} />
        <span>{t("viewers.columns")}</span>
        <span className="tabular-nums text-white/50">
          {shownCount}/{columns.length}
        </span>
      </button>
      {open && (
        <div
          className="absolute right-0 z-30 mt-1 max-h-80 w-64 overflow-auto rounded-md border border-white/10 bg-[#0f1d32] py-2 shadow-lg ring-1 ring-white/10"
          role="listbox"
          aria-label={t("viewers.visibleColumns")}
        >
          <div className="border-b border-white/5 px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-white/65">{t("viewers.showColumns")}</div>
          <ul className="py-1">
            {columns.map((col) => {
              const checked = visible.has(col.field);
              const disableUncheck = checked && visible.size <= 1;
              return (
                <li key={col.field}>
                  <label
                    className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-white/5 ${
                      disableUncheck ? 'opacity-60' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="rounded border-white/20 text-brand-blue-light focus:ring-brand-blue-light"
                      checked={checked}
                      disabled={disableUncheck}
                      onChange={() => toggle(col.field)}
                    />
                    <span className="truncate" title={columnTooltip(col)}>
                      {col.label}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
